import { useCallback, useState } from 'react';
import { ApiClientError } from './api.js';

/**
 * A write against the API: busy flag, typed failure, and the result.
 *
 * `error` is the ApiClientError itself rather than just its code, because the
 * callers need `details` — which field clashed, what is blocking — to say
 * something more useful than "conflict".
 */
export function useAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);

  const run = useCallback(
    async (...args: Args): Promise<Result | null> => {
      setBusy(true);
      setError(null);
      try {
        return await action(...args);
      } catch (e) {
        setError(
          e instanceof ApiClientError
            ? e
            : new ApiClientError('internal', '', e instanceof Error ? e.message : String(e)),
        );
        return null;
      } finally {
        setBusy(false);
      }
    },
    [action],
  );

  const reset = useCallback(() => setError(null), []);

  return { run, busy, error, reset };
}
